
import Vue from "vue";
import { CodaBoxService } from "@/services/codabox-service";
import { CodaBoxDocument } from "@/models/codabox-document";
import { ICodaImportResult } from "@/services/coda-service";
import { Bank, BankType } from "@/models/base";
import { BankService } from "@/services/bank-service";
import { Axios } from "@/utils/axios";
import { downloadBlob } from "@/utils/blob";
import { IDataTableColumn } from "winfakt-vue-components";
import AppCodaPreview from "./CodaPreview.vue";
import AppSelectBankType from "./SelectBankType.vue";
import { printHumanFloat } from "@/utils/pretty-print";

export default Vue.extend({
	data() {
		return {
			step: 1,
			activeTab: 0,
			documents: [] as CodaBoxDocument[],
			orderBy: "StatementNr" as string | null,
			orderAscending: true,
			syncing: false,
			selectedDoc: null as CodaBoxDocument | null,
			selectedBankId: 1,
			loadingPreview: false,
			previewResult: null as ICodaImportResult | null,
			bankTypes: [] as BankType[],
		};
	},
	computed: {
		tabs(): string[] {
			return [
				this.$ct("codabox-tab-pending").toString(),
				this.$ct("codabox-tab-processed").toString(),
			];
		},
		statusFilter(): string {
			return this.activeTab === 0 ? "pending" : "processed";
		},
		sortedDocuments(): CodaBoxDocument[] {
			if (!this.orderBy) return this.documents;
			let prop = this.orderBy as keyof CodaBoxDocument;
			let dir = this.orderAscending ? 1 : -1;
			return [...this.documents].sort((a, b) => {
				let aVal = (a[prop] || "") as string;
				let bVal = (b[prop] || "") as string;
				let aNum = Number(aVal);
				let bNum = Number(bVal);
				if (aVal !== "" && bVal !== "" && !isNaN(aNum) && !isNaN(bNum)) {
					return (aNum - bNum) * dir;
				}
				return aVal < bVal ? -dir : aVal > bVal ? dir : 0;
			});
		},
		// eslint-disable-next-line max-lines-per-function
		columns(): IDataTableColumn[] {
			let self = this;
			return [
				{
					name: "Type",
					property: "DocumentType",
					width: "80px",
					print(value: string): string {
						return value.toUpperCase();
					}
				},
				{
					name: "IBAN",
					property: "IBAN",
					width: "200px",
				},
				{
					name: this.$ct("codabox-bank-name").toString(),
					property: "IBAN",
					width: "180px",
					print: (value: string): string => {
						let bank = this.bankTypes.find(b => b.isSameIban(value));
						return bank ? bank.Name : "";
					},
					disableSorting: true,
				},
				{
					name: this.$ct("models.friendlyNames.Bank.StNr").toString(),
					property: "StatementNr",
					width: "100px",
				},
				{
					name: "Datum",
					property: "BalanceDate",
					width: "120px",
				},
				{
					name: "",
					property: "ID",
					width: "250px",
					disableSorting: true,
					print(value: number, item: CodaBoxDocument): string {
						let buttons = `<div style="text-align:right">`;
						if (item.isPending) {
							if (item.isCoda) {
								buttons += `<button class="codabox-action-btn codabox-preview-btn" data-action="preview" data-id="${item.ID}">` +
									`<i class="fas fa-cogs"></i> ${self.$ct("codabox-process")}` +
									`</button> `;
							}
							if (item.isSoda) {
								buttons += `<button class="codabox-action-btn codabox-download-btn" data-action="download" data-id="${item.ID}">` +
									`<i class="fas fa-download"></i> ${self.$ct("codabox-download")}` +
									`</button> `;
							}
							buttons += `<button class="codabox-action-btn codabox-skip-btn" data-action="skip" data-id="${item.ID}">` +
								`<i class="fas fa-forward"></i> ${self.$ct("codabox-skip")}` +
								`</button>`;
						} else {
							buttons += `<button class="codabox-action-btn codabox-download-btn" data-action="download" data-id="${item.ID}">` +
								`<i class="fas fa-download"></i> ${self.$ct("codabox-download")}` +
								`</button> `;
							buttons += `<button class="codabox-action-btn codabox-reset-btn" data-action="reset" data-id="${item.ID}">` +
								`<i class="fas fa-undo"></i> ${self.$ct("codabox-reset")}` +
								`</button>`;
						}
						buttons += `</div>`;
						return buttons;
					}
				}
			];
		}
	},
	watch: {
		activeTab() {
			this.fetchDocuments();
		},
	},
	methods: {
		onOrder(event: { order: string; ascending: boolean }) {
			this.orderBy = event.order;
			this.orderAscending = event.ascending;
		},
		close() {
			this.$emit("close");
		},
		async sync() {
			if (this.syncing) return;
			this.syncing = true;
			try {
				let result = await CodaBoxService.sync();
				await this.fetchDocuments();
				let msg = this.$ct("codabox-synced", {
					count: result.Count,
					skipped: result.SkippedCount
				});
				this.$wf.notify(msg, "success", 3000, "check");
			} catch (err) {
				this.$wf.notify(this.$ct("common.error"), "danger", 3000, "exclamation-triangle");
			} finally {
				this.syncing = false;
			}
		},
		async fetchDocuments() {
			try {
				this.documents = await CodaBoxService.getDocuments(this.statusFilter);
			} catch (err) {
				this.$wf.notify(this.$ct("common.error"), "danger", 3000, "exclamation-triangle");
			}
		},
		startPreview(doc: CodaBoxDocument) {
			this.selectedDoc = doc;
			this.previewResult = null;
			if (doc.IBAN) {
				let match = this.bankTypes.find(b => b.isSameIban(doc.IBAN));
				if (match) {
					this.selectedBankId = match.ID;
				}
			}
			this.step = 2;
		},
		backToList() {
			this.step = 1;
			this.selectedDoc = null;
			this.previewResult = null;
		},
		/* eslint-disable-next-line max-lines-per-function */
		async loadPreview() {
			if (!this.selectedDoc || this.loadingPreview) return;
			this.loadingPreview = true;
			try {
				let result = await CodaBoxService.getPreview(this.selectedDoc.ID, this.selectedBankId);

				if (result.Banks.length === 0) {
					await this.$wf.dialog(this.$ct("coda-has-no-records"), this.$ct("common.ok"), this.$ct("common.warning"), "warning");
					return;
				}

				// Attach bank types to banks
				result.Banks.map((b: Bank) => {
					b.Type = this.bankTypes.filter((c) => c.ID == b.TypeID)[0];
				});

				let currentBalance = await BankService.getBalance(this.selectedBankId);
				if (result.BeginBalance != currentBalance) {
					let confirm = await this.$confirmBuilder().setClass("warning").setWarningTitle().setMessage(this.$ct("coda-balance-does-not-match", {
						winfakt: printHumanFloat(currentBalance),
						coda: printHumanFloat(result.BeginBalance)
					})).setYesText(this.$ct("common.continue")).setNoText(this.$ct("common.cancel")).show();
					if (!confirm) {
						return;
					}
				}

				let fileUploadedBefore = await BankService.isStNrUsed(result.Banks[0].StNr, this.selectedBankId);
				if (fileUploadedBefore) {
					let confirm = await this.$confirmBuilder().setClass("warning").setWarningTitle().setMessage(this.$ct("coda-file-already-imported"))
						.setYesText(this.$ct("common.continue")).setNoText(this.$ct("common.cancel")).show();
					if (!confirm) {
						return;
					}
				}

				this.previewResult = result;
			} catch (err) {
				this.$wf.notify(this.$ct("common.error"), "danger", 3000, "exclamation-triangle");
			} finally {
				this.loadingPreview = false;
			}
		},
		async confirmDocument(items: Bank[]): Promise<void> {
			if (!this.selectedDoc) return;
			await CodaBoxService.confirmDocument(this.selectedDoc.ID, items);
		},
		async onConfirmDone() {
			let endBalance = this.previewResult ? this.previewResult.EndBalance : 0;
			this.$wf.notify(this.$ct("codabox-confirmed"), "success", 3000, "check");

			let currentBalance = await BankService.getBalance(this.selectedBankId);
			if (currentBalance.toFixed(2) != endBalance.toFixed(2)) {
				await this.$wf.dialog(this.$ct("coda-end-balance-and-current-balance-mismatch", {
					winfakt: currentBalance.toFixed(2),
					coda: endBalance.toFixed(2)
				}), this.$ct("common.ok"), this.$ct("common.warning"), "warning");
			}

			this.$emit("refresh");
			this.backToList();
			await this.fetchDocuments();
		},
		async skipDocument(doc: CodaBoxDocument) {
			let confirm = await this.$wf.confirm(
				this.$ct("codabox-confirm-skip"),
				this.$ct("common.yes"),
				this.$ct("common.no"),
				this.$ct("common.warning"),
				"warning"
			);
			if (!confirm) return;
			try {
				await CodaBoxService.skipDocument(doc.ID);
				this.$wf.notify(this.$ct("codabox-skipped"), "success", 3000, "check");
				await this.fetchDocuments();
			} catch (err) {
				this.$wf.notify(this.$ct("common.error"), "danger", 3000, "exclamation-triangle");
			}
		},
		async downloadDocument(doc: CodaBoxDocument) {
			try {
				let url = CodaBoxService.getDownloadUrl(doc.ID);
				let response = await Axios.get(url, { responseType: "blob" });
				let contentDisposition = response.headers["content-disposition"] || "";
				let filename = "codabox-document.pdf";
				let match = contentDisposition.match(/filename=(.+)/);
				if (match) {
					filename = match[1];
				}
				let blob = new Blob([response.data], { type: response.headers["content-type"] || "application/pdf" });
				downloadBlob(blob, filename);
			} catch (err) {
				this.$wf.notify(this.$ct("common.error"), "danger", 3000, "exclamation-triangle");
			}
		},
		async resetDocument(doc: CodaBoxDocument) {
			try {
				await CodaBoxService.resetDocument(doc.ID);
				this.$wf.notify(this.$ct("codabox-reset-done"), "success", 3000, "check");
				await this.fetchDocuments();
			} catch (err) {
				this.$wf.notify(this.$ct("common.error"), "danger", 3000, "exclamation-triangle");
			}
		},
		handleTableClick(event: Event) {
			let target = event.target as HTMLElement;
			let btn = target.closest("[data-action]") as HTMLElement | null;
			if (!btn) return;

			let action = btn.dataset.action;
			let id = parseInt(btn.dataset.id || "0");
			let doc = this.documents.find(d => d.ID === id);
			if (!doc) return;

			if (action === "preview") {
				this.startPreview(doc);
			} else if (action === "download") {
				this.downloadDocument(doc);
			} else if (action === "skip") {
				this.skipDocument(doc);
			} else if (action === "reset") {
				this.resetDocument(doc);
			}
		}
	},
	async mounted() {
		this.bankTypes = BankService.getTypes().filter(b => b.ShowOnCodaImport);
		if (this.bankTypes.length > 0) {
			this.selectedBankId = this.bankTypes[0].ID;
		}
		await this.fetchDocuments();
		this.$el.addEventListener("click", this.handleTableClick);
	},
	beforeDestroy() {
		this.$el.removeEventListener("click", this.handleTableClick);
	},
	components: {
		AppCodaPreview,
		AppSelectBankType,
	},
	i18nEx: {
		componentPrefix: "components.bank"
	}
});
